<template>
	<div class="edit-app">
		<HeaderLoader :is-loading="detailsState.isLoading.value || updating" />
		<div
			v-if="
				authStore.user &&
				authStore.user.id === 1 &&
				detailsState.details.value?.creator &&
				authStore.user?.id !== detailsState.details.value?.creator?.id
			"
			class="edit-app__user-warning">
			WARNING: Viewing app from user {{ detailsState.details.value?.creator?.email }}. Any changes
			to this app will result in changes to the customer's application.
		</div>
		<Container
			v-if="detailsState.isLoaded.value && app && module && selectedModuleName"
			class="edit-app__container">
			<ModulesSidebar
				v-model="selectedModuleName"
				:app
				:readonly="isAppReadonly"
				@new-module="addNewModule"
				@update-order="updateBuild" />
			<div
				:key="selectedModuleName"
				class="edit-app__columns-relations-container">
				<div
					v-if="selectedModuleName"
					class="edit-app__columns-relations">
					<ModuleHeader
						:app
						:module
						:readonly="isAppReadonly || isModuleReadonly"
						:updating
						@icon-updated="updateBuild()"
						@module-removed="handleModuleRemoved()"
						@name-updated="((selectedModuleName = $event), updateBuild())" />
					<ColumnsContainer
						:app
						:module
						:readonly="isAppReadonly || isModuleReadonly"
						@update="updateBuild()" />
					<RelationsContainer
						:app
						:module
						:readonly="isAppReadonly || isModuleReadonly"
						@update="updateBuild()" />
				</div>
				<Message
					v-if="module?.name === 'User'"
					class="edit-app__user-info-message"
					icon="fal fa-lightbulb-on"
					severity="secondary"
					size="small"
					>The User module is pre-defined and cannot be removed. <br />
					Create relations to and from other modules to connect entities to the app's
					users.</Message
				>
			</div>
		</Container>
		<DeploymentControls
			v-if="!detailsState.details.value?.archived"
			:build="activeBuild"
			:building="building"
			:deployment="activeDeployment"
			@build-requested="buildVersion" />
	</div>
</template>

<script setup lang="ts">
import DeploymentControls from './EditApp/DeploymentControls.vue'
import { useRoute, useRouter } from 'vue-router'
import { ref, watch } from 'vue'
import { computed } from 'vue'
import { useAuthStore } from '@/stores/Auth'
import HeaderLoader from '@/components/HeaderLoader.vue'
import Container from '@/components/Container.vue'
import ModulesSidebar from './EditApp/ModulesSidebar.vue'
import { useToast } from 'primevue/usetoast'
import type { AppDetailsState } from '@/models/App/States'
import BuildsApi from '@/models/Build/Api'
import Message from 'primevue/message'
import { usePostHog } from '@/composables/usePostHog.js'
import ColumnsContainer from './EditApp/ColumnsContainer.vue'
import ModuleHeader from './EditApp/ModuleHeader.vue'
import RelationsContainer from './EditApp/RelationsContainer.vue'
import { EnrichedApp } from '@codecannon/schemas/schemas/EnrichedApp'
import type { EnrichedModule } from '@codecannon/schemas/schemas/EnrichedModule'
import { useSchemaValidation } from '@/views/App/composables/useSchemaValidation'

const props = defineProps<{ detailsState: AppDetailsState; app: EnrichedApp | null }>()
const emit = defineEmits<{
	(e: 'get-details'): void
}>()

// Loaders
const updating = ref<boolean>(false)
const building = ref<boolean>(false)

// Composables
const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const toast = useToast()
const detailsState = props.detailsState
const { posthog } = usePostHog()
const { validateSchema } = useSchemaValidation()
const { activeBuild, activeBuildId, updateBuild, buildVersion } = useBuild()
const { activeDeployment } = useDeployment()
const { selectedModuleName, module, addNewModule, handleModuleRemoved, isModuleReadonly } =
	useModule()
const { isAppReadonly } = useApp()

function useApp() {
	const isAppReadonly = computed(() => !!detailsState.details.value?.archived)

	return { isAppReadonly }
}

function useBuild() {
	const activeBuild = computed(() => detailsState.details.value?.latest_build)
	const activeBuildId = computed(() => activeBuild.value?.id)

	async function createNewDraftBuild() {
		await new BuildsApi().store({
			app_id: Number(route.params.id as string),
			draft: true,
			parent_build_id: Number(activeBuild.value!.id),
			app_schema: props.app?.toJSON(),
		})
		emit('get-details')
	}

	async function updateBuild() {
		updating.value = true
		try {
			if (!activeBuild.value?.draft) {
				await createNewDraftBuild()
			} else {
				await new BuildsApi().update(activeBuildId.value as number, {
					app_schema: props.app?.toJSON(),
				})
			}
		} finally {
			updating.value = false
		}
	}

	async function buildVersion() {
		if (!activeBuildId.value) return
		if (!validateSchema(props.app, route.params.id as string)) return

		building.value = true
		try {
			await new BuildsApi().update(activeBuildId.value, { draft: false })
			emit('get-details')
		} catch (error: any) {
			if (error.response?.status === 429) {
				toast.add({
					severity: 'error',
					summary: 'App build limit reached',
					detail: 'Only one build at a time allowed to be in progress.',
					life: 5000,
				})
			}
		} finally {
			building.value = false
			posthog.capture('application_built') // In finally block so the button can't be spammed
		}
	}

	return {
		activeBuildId,
		activeBuild,
		updateBuild,
		buildVersion,
	}
}

function useDeployment() {
	const activeDeployment = computed(() => activeBuild.value?.latest_deployment)

	return {
		activeDeployment,
	}
}

function useModule() {
	const selectedModuleName = ref<string | null>('')
	const module = computed(() => {
		if (!selectedModuleName.value) return null
		return props.app?.moduleRegistry.get(selectedModuleName.value)
	})
	const isModuleReadonly = computed(() => module.value?.name === 'User')

	watch(
		[selectedModuleName, () => route.query.module],
		([newSelectedModuleName, newQueryModule], [oldSelectedModuleName, oldQueryModule]) => {
			if (!selectedModuleName.value) return
			const query = route.query
			if (newSelectedModuleName !== oldSelectedModuleName) {
				router.replace({ query: { ...query, tab: 'edit', module: selectedModuleName.value } })
			} else if (newQueryModule !== oldQueryModule) {
				selectedModuleName.value = newQueryModule as string
			}
		},
		{ immediate: true },
	)

	watch(
		() => activeBuildId.value,
		async () => {
			if (
				route.query.module &&
				!Array.isArray(route.query.module) &&
				props.app?.modules.some((m) => m.name === route.query.module)
			) {
				selectedModuleName.value = route.query.module
			}
			if (!props.app?.modules.some((m) => m.name === selectedModuleName.value)) {
				selectedModuleName.value = props.app?.modules[0]!.name ?? null
			}
		},
		{ immediate: true },
	)

	async function addNewModule(module: EnrichedModule) {
		props.app?.addModule(module, props.app?.modules.length - 2)
		selectedModuleName.value = module.name

		updateBuild()
	}

	function handleModuleRemoved() {
		if (!props.app) return
		selectedModuleName.value = props.app.modules[0]!.name
		updateBuild()
	}

	return {
		selectedModuleName,
		module,
		addNewModule,
		handleModuleRemoved,
		isModuleReadonly,
	}
}
</script>

<style scoped lang="scss">
.edit-app {
	display: flex;
	flex-direction: column;
	align-items: center;

	.edit-app__user-warning {
		background: var(--p-red-500);
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 16px;
		color: white;
		width: 100%;
		font-weight: bold;
	}

	.edit-app__container {
		display: flex;
		flex-direction: column;
		align-items: start;
		flex-direction: row;
		width: 100%;
		gap: 16px;
		padding-bottom: 0;

		.edit-app__columns-relations-container {
			flex: 1;
			display: flex;
			flex-direction: column;
			gap: 5px;
			padding-bottom: 114px;

			.edit-app__columns-relations {
				flex: 1;
				display: flex;
				flex-direction: column;
				border-radius: 10px;
				padding: 20px;
				box-shadow: var(--p-card-shadow);
				background-color: var(--p-surface-0);
				gap: 16px;

				@media all {
					background-color: var(--p-surface-900);
				}
			}
		}

		.edit-app__user-info-message {
			margin-top: 8px;
			margin-bottom: 6px;

			:deep(.p-message-content) {
				gap: 10px;

				.p-message-icon {
					width: auto;
				}
			}
		}
	}
}
</style>
